'use client';

import { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { integrationsService } from '@/services/integrations.service';
import { clientsService } from '@/services/clients.service';
import { useAuth } from '@/lib/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Check, MoreVertical, Plus, RefreshCw, Users, X } from 'lucide-react';
import type { Integration, GitHubRepository, Client } from '@/types';

// GitHub Icon SVG
const GitHubIcon = ({ className = 'w-6 h-6' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

function getTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function IntegrationsPage() {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  // Modal state for repo selection
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [repos, setRepos] = useState<GitHubRepository[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [selectedRepos, setSelectedRepos] = useState<string[]>([]);

  // State for client linking
  const [clients, setClients] = useState<Client[]>([]);
  const [linkingIntegration, setLinkingIntegration] = useState<Integration | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  const isGitHubConnected = integrations.some((i) => i.provider === 'github');

  useEffect(() => {
    if (!user) return;

    loadIntegrations();
    loadClients();

    // Check for GitHub callback params
    const urlParams = new URLSearchParams(window.location.search);
    const installationId = urlParams.get('installation_id');
    const setupAction = urlParams.get('setup_action');

    if (installationId && setupAction) {
      handleGitHubCallback(parseInt(installationId), setupAction as 'install' | 'update' | 'request');
      // Clean up URL
      window.history.replaceState({}, '', '/settings/integrations');
    }
  }, [user]);

  async function loadClients() {
    try {
      const data = await clientsService.getClients();
      setClients(data);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  }

  async function loadIntegrations() {
    try {
      setLoading(true);
      const data = await integrationsService.getIntegrations();
      setIntegrations(data);
    } catch (err) {
      setError('Failed to load integrations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleConnectGitHub() {
    try {
      setConnecting(true);
      const { url } = await integrationsService.getGitHubInstallUrl();
      window.location.href = url;
    } catch (err) {
      setError('Failed to get GitHub installation URL');
      console.error(err);
      setConnecting(false);
    }
  }

  async function handleGitHubCallback(installationId: number, setupAction: 'install' | 'update' | 'request') {
    try {
      setConnecting(true);
      await integrationsService.processGitHubCallback({ installation_id: installationId, setup_action: setupAction });
      await loadIntegrations();
    } catch (err) {
      setError('Failed to complete GitHub connection');
      console.error(err);
    } finally {
      setConnecting(false);
    }
  }

  async function handleSync(integrationId: string) {
    try {
      setSyncing(integrationId);
      const result = await integrationsService.syncIntegration(integrationId);
      await loadIntegrations();

      if (result.errors.length > 0) {
        setError(`Sync completed with errors: ${result.errors[0]}`);
      }
    } catch (err) {
      setError('Failed to sync integration');
      console.error(err);
    } finally {
      setSyncing(null);
    }
  }

  async function handleDisconnect(integrationId: string) {
    if (!confirm('Are you sure you want to disconnect this integration? Your synced tasks will remain.')) {
      return;
    }

    try {
      await integrationsService.disconnectIntegration(integrationId);
      await loadIntegrations();
    } catch (err) {
      setError('Failed to disconnect integration');
      console.error(err);
    }
  }

  async function handleManageRepos(integration: Integration) {
    setSelectedIntegration(integration);
    setSelectedRepos(integration.syncRepos);
    setLoadingRepos(true);

    try {
      const repoList = await integrationsService.listGitHubRepos(integration.id);
      setRepos(repoList);
    } catch (err) {
      console.error('Failed to load repos:', err);
    } finally {
      setLoadingRepos(false);
    }
  }

  async function handleSaveRepos() {
    if (!selectedIntegration) return;

    try {
      await integrationsService.updateIntegration(selectedIntegration.id, {
        syncRepos: selectedRepos,
      });
      await loadIntegrations();
      setSelectedIntegration(null);
    } catch (err) {
      setError('Failed to update repository settings');
      console.error(err);
    }
  }

  async function handleToggleSync(integration: Integration) {
    try {
      await integrationsService.updateIntegration(integration.id, {
        syncEnabled: !integration.syncEnabled,
      });
      await loadIntegrations();
    } catch (err) {
      setError('Failed to update sync settings');
      console.error(err);
    }
  }

  function handleLinkToClient(integration: Integration) {
    setLinkingIntegration(integration);
    setSelectedClientId(integration.clientId || '__none__');
  }

  async function handleSaveClientLink() {
    if (!linkingIntegration) return;

    try {
      await integrationsService.updateIntegration(linkingIntegration.id, {
        clientId: selectedClientId === '__none__' ? null : selectedClientId,
      });
      await loadIntegrations();
      setLinkingIntegration(null);
    } catch (err) {
      setError('Failed to link integration to client');
      console.error(err);
    }
  }

  function getClientName(clientId: string | undefined): string | undefined {
    if (!clientId) return undefined;
    const client = clients.find(c => c.id === clientId);
    return client?.name;
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-4 w-96 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        <Breadcrumb
          items={[
            { label: 'Settings', href: '/settings' },
            { label: 'Integrations' },
          ]}
        />

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-2">Integrations</h1>
          <p className="text-muted-foreground">
            Connect external services to automatically sync issues and tasks
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-destructive hover:text-destructive/80">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Available Integrations - Selectable Cards */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4">Available Integrations</h2>
          <div className="grid grid-cols-1 gap-4">
            {/* GitHub Card */}
            <Card
              className={`relative cursor-pointer transition-all ${
                isGitHubConnected
                  ? 'border-primary bg-primary/5'
                  : 'hover:border-primary/50 hover:bg-muted/50'
              }`}
              onClick={() => !isGitHubConnected && !connecting && handleConnectGitHub()}
            >
              {isGitHubConnected && (
                <div className="absolute top-4 right-4">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground">
                    <Check className="w-4 h-4" />
                  </div>
                </div>
              )}
              <CardHeader className="flex flex-row items-center gap-4 pb-2">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#24292e] text-white">
                  <GitHubIcon className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">GitHub</CardTitle>
                  <CardDescription>
                    Sync issues from your GitHub repositories as tasks
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                {isGitHubConnected ? (
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    Connected
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    disabled={connecting}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConnectGitHub();
                    }}
                  >
                    {connecting ? 'Connecting...' : 'Connect'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Connected Integrations */}
        {integrations.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Connected Accounts</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handleConnectGitHub}
                disabled={connecting}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add More
              </Button>
            </div>
            <div className="space-y-4">
              {integrations.map((integration) => (
                <Card key={integration.id}>
                  {/* Client Header - shown at top if linked */}
                  {integration.clientId && (
                    <div className="px-6 pt-4 pb-2 border-b border-border">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium text-foreground">{getClientName(integration.clientId)}</span>
                      </div>
                    </div>
                  )}
                  <CardContent className={integration.clientId ? 'p-6 pt-4' : 'p-6'}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        {/* Avatar */}
                        {integration.avatarUrl ? (
                          <img
                            src={integration.avatarUrl}
                            alt={integration.accountLogin}
                            className="w-12 h-12 rounded-full"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-[#24292e] rounded-full flex items-center justify-center text-white">
                            <GitHubIcon />
                          </div>
                        )}

                        {/* Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-foreground">{integration.accountLogin}</h3>
                            <Badge variant="outline">{integration.accountType}</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {integration.syncRepos.length === 0
                              ? 'All repositories'
                              : `${integration.syncRepos.length} repositories`}
                          </div>
                          {integration.lastSyncAt && (
                            <div className="flex items-center gap-2 mt-2">
                              <Badge
                                variant={integration.lastSyncStatus === 'success' ? 'default' : 'destructive'}
                                className="text-xs"
                              >
                                {integration.lastSyncStatus === 'success' ? 'Synced' : 'Failed'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {getTimeAgo(new Date(integration.lastSyncAt))}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* Sync toggle */}
                        <Button
                          variant={integration.syncEnabled ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleToggleSync(integration)}
                        >
                          {integration.syncEnabled ? 'Sync On' : 'Sync Off'}
                        </Button>

                        {/* Sync now button */}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleSync(integration.id)}
                          disabled={syncing === integration.id || !integration.syncEnabled}
                        >
                          {syncing === integration.id ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Syncing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Sync Now
                            </>
                          )}
                        </Button>

                        {/* More options dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleManageRepos(integration)}>
                              Manage Repositories
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleLinkToClient(integration)}>
                              {integration.clientId ? 'Change Client' : 'Link to Client'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDisconnect(integration.id)}
                              className="text-destructive"
                            >
                              Disconnect
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Repository Selection Modal */}
        {selectedIntegration && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
              <CardHeader>
                <CardTitle>Select Repositories</CardTitle>
                <CardDescription>
                  Choose which repositories to sync. Leave empty to sync all.
                </CardDescription>
              </CardHeader>

              <CardContent className="flex-1 overflow-y-auto">
                {loadingRepos ? (
                  <div className="text-center py-8 text-muted-foreground">Loading repositories...</div>
                ) : repos.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No repositories found</div>
                ) : (
                  <div className="space-y-2">
                    {repos.map((repo) => (
                      <label
                        key={repo.id}
                        className="flex items-center gap-3 p-3 hover:bg-muted rounded-lg cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRepos.includes(repo.fullName)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRepos([...selectedRepos, repo.fullName]);
                            } else {
                              setSelectedRepos(selectedRepos.filter((r) => r !== repo.fullName));
                            }
                          }}
                          className="w-4 h-4 rounded border-border"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-foreground">{repo.name}</div>
                          <div className="text-sm text-muted-foreground">{repo.fullName}</div>
                        </div>
                        {repo.private && (
                          <Badge variant="outline" className="text-xs">Private</Badge>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </CardContent>

              <div className="p-6 border-t border-border flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setSelectedIntegration(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveRepos}>
                  Save Selection
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Client Link Modal */}
        {linkingIntegration && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-md w-full mx-4">
              <CardHeader>
                <CardTitle>Link to Client</CardTitle>
                <CardDescription>
                  Assign this integration ({linkingIntegration.accountLogin}) to a client.
                  Tasks synced from this account will be associated with the selected client.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No client (unlink)</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>

              <div className="p-6 border-t border-border flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setLinkingIntegration(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveClientLink}>
                  Save
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
